import { useEffect, useState } from 'react'
import { Users, Send, ArrowLeft, PartyPopper, Clock, CheckCircle2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import KempenvisionNav from '@/components/KempenvisionNav'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select'
import { visionAPI } from '../lib/api'
import { useVisionSocket } from '../lib/visionSocket'
import TeamMark, { parseFlag } from '@/components/TeamMark'

const NO_POINTS = '__none__'



export default function KempenvisionVotePage() {
  const [data, setData]     = useState(null)
  const [votes, setVotes]   = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState(null)
  const [loading, setLoading] = useState(true)

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await visionAPI.getMyVoteState()
      setData(data)
      if (data.my_votes?.length) {
        const v = {}; data.my_votes.forEach(x => { v[x.entry_id] = Number(x.points) })
        setVotes(v)
      }
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])
  useVisionSocket((p) => { if (p.scope === 'show' || p.scope === 'vote') load() })

  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>
  if (!data) return <div className="p-6 text-red-500">Kon niet laden.</div>
  if (!data.show) {
    return (
      <div className="max-w-lg mx-auto">
        <KempenvisionNav />
        <Card><CardContent className="p-6 text-center text-muted-foreground">
          Geen actieve Kempenvision show.
        </CardContent></Card>
      </div>
    )
  }

  const { show, entries, my_votes, is_open, already_voted, can_vote, available_points } = data
  const usedPoints = new Set(Object.values(votes).filter(x => x != null))

  const changePoints = (entryId, val) => {
    if (!can_vote) return
    setError(null)
    setVotes(prev => {
      const next = { ...prev }
      if (val === NO_POINTS || val === '' || val == null) delete next[entryId]
      else next[entryId] = Number(val)
      return next
    })
  }

  const submit = async () => {
    const payload = Object.entries(votes)
      .filter(([, p]) => p != null)
      .map(([entry_id, points]) => ({ entry_id: Number(entry_id), points }))
    if (payload.length === 0) { setError('Geef minstens 1 punt'); return }
    if (!confirm('Verzenden? Je kan hierna niet meer wijzigen.')) return
    setSaving(true); setError(null)
    try {
      await visionAPI.submitMyVote(payload)
      await load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setSaving(false) }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-32">
      <KempenvisionNav />
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0" style={{ borderLeft: "2px solid rgb(var(--acid-rgb))" }}>
          <Users className="h-6 w-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">Publiek stem</div>
          <h1 className="text-xl font-bold truncate">{show.name}{show.edition ? ` — ${show.edition}` : ''}</h1>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/kempenvision"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
      </div>

      {/* Status banners */}
      {already_voted && (
        <Card className="border-emerald-500/40 bg-emerald-500/10">
          <CardContent className="p-4 flex items-center gap-3">
            <PartyPopper className="h-6 w-6 text-emerald-400 shrink-0" />
            <div>
              <div className="font-bold text-emerald-400">Bedankt voor je stem!</div>
              <div className="text-sm text-muted-foreground">Je hebt {my_votes.length} punt-waardes verdeeld. Je kan de uitslag straks op de beamer volgen.</div>
            </div>
          </CardContent>
        </Card>
      )}
      {!already_voted && !is_open && (
        <Card className="border-yellow-500/40 bg-yellow-500/10">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-6 w-6 text-yellow-500 shrink-0" />
            <div>
              <div className="font-bold text-yellow-500">Publiek stem is nog niet open</div>
              <div className="text-sm text-muted-foreground">Publiek stemmen is momenteel dicht. Wacht tot de operator het opent.</div>
            </div>
          </CardContent>
        </Card>
      )}
      {can_vote && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-400" /> Verdeel je punten
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2 text-sm">
            <p className="text-muted-foreground">
              Geef aan welke entries je het beste vond. Elke waarde kan maar <b>1x</b>. Je hoeft niet alle punten te gebruiken.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {available_points.slice().reverse().map(p => (
                <Badge key={p} variant={usedPoints.has(p) ? 'default' : 'outline'}
                       className={usedPoints.has(p) ? 'bg-emerald-500 text-black text-base px-3' : 'text-muted-foreground'}>
                  {p}
                </Badge>
              ))}
            </div>
            <div className="text-xs text-muted-foreground pt-1">
              {usedPoints.size}/{available_points.length} punt-waardes toegekend
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entry list */}
      <div className="space-y-2">
        {entries.map(e => {
          const flag = parseFlag(e.team_flag_colors)
          const current = votes[e.id] ?? null
          const highlight = current != null
          return (
            <Card key={e.id} className={highlight ? 'border-emerald-500/40' : ''}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="font-mono text-xs text-muted-foreground w-6 text-center shrink-0">#{e.running_order}</div>
                <TeamMark flag_colors={flag} logo_url={e.team_logo_url} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {e.team_name} · <span className="uppercase">{e.genre}</span>
                  </div>
                </div>
                <div className="shrink-0 w-24 sm:w-32">
                  {(can_vote || (already_voted && current)) ? (
                    <Select
                      value={current == null ? NO_POINTS : String(current)}
                      onValueChange={(v) => changePoints(e.id, v)}
                      disabled={!can_vote}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_POINTS}>— geen —</SelectItem>
                        {available_points.slice().reverse().map(p => {
                          const disabled = usedPoints.has(p) && p !== current
                          return (
                            <SelectItem key={p} value={String(p)} disabled={disabled}>
                              {p} punten{disabled ? ' (bezet)' : ''}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  ) : current != null ? (
                    <Badge className="bg-emerald-500 text-black text-lg">{current}</Badge>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {entries.length === 0 && (
        <Card><CardContent className="p-6 text-center text-muted-foreground">
          Nog geen entries in deze show.
        </CardContent></Card>
      )}

      {can_vote && (
        <div className="fixed bottom-0 inset-x-0 bg-background/95 backdrop-blur border-t border-border p-3 z-50">
          <div className="max-w-2xl mx-auto flex gap-2 items-center">
            <div className="flex-1 text-sm">
              {error ? <span className="text-red-500">✕ {error}</span>
                     : <span className="text-muted-foreground">{usedPoints.size} van {available_points.length} punten</span>}
            </div>
            <Button size="lg" onClick={submit} disabled={saving || usedPoints.size === 0}>
              <Send className="h-4 w-4 mr-2" />{saving ? 'Verzenden…' : 'Verzend'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
